/**
 * Events
 * Create the Events section
 *
 * Usage
 * - items is the main prop that gets all the events
 * - Accepts a GraphQL feed from contentful to populate the list of events, this
 *   list is used create the month association so its dynamic.
 *
 * GraphQL
 * ContentfulEvent {
 *   title
 *   startDate
 *   endDate
 *   locationAddress
 *   eventType
 *   locationName
 *   image {
 *     title
 *     fixed {
 *       src
 *     }
 *   }
 * }
 *
 * Features
 * - Filterable event Types (One selectedable only)
 * - Sortable, current and past
 *   - current endDate > DateNow
 *   - past endDate < DateNow
 *   - If no end date then assume current
 *
 * @todo Refactor: could add the month key into the reduced filter
 * @todo Seems I get the whole events list back so interactions here is just dealing with the graphql stack
 * @todo Background image from Fitch CDN is 700KB ! this should be optimised
 * @todo Images need fixed here - they are huge in filesize
 * @todo Currently have a nested section cause the Landing page brings this in with the section, but at that stage I cannot add the bits needed
 * @todo Not sure these lists comply with the List component so hardlining in EventFilters
 * @todo the Date sorting only needs to run intially on first render - now I am running each time sort or filtered
 */

import React, { Component, Fragment } from 'react'

import includes from 'lodash/includes'

import { Hash, Header, FormatMonth } from '../..'
import { withScroller } from '../../utils/withScroller'
import { EventListing, EventProps } from './EventListing'
import { EventListingsFilters } from './EventListingsFilters'
import { EventListingsMonths } from './EventListingsMonths'
import {
  EventListingsFilterType,
  FilterSubItemProps
} from './EventListingsFilterType'

import { eventTypes, sortTypes, filSecRegTypes } from './constants'

export type EventRegSec = {
  items: any[]
  value: string
}
export type EventsProps = {
  secFilType: EventRegSec
  regFilType: EventRegSec
  isHomePage?: boolean
  items: EventProps[]
  eventLangSlug: string
  eventsDataLoading?: boolean
  hideFilters?: boolean
  bgImage?: string
}
interface EventsState {
  activeMonth: string
  filter: string[]
  filteredItems: EventProps[]
  isLoading: boolean
  months: string[]
  sort: string
  filSecReg: string
  isFilterType: boolean
  selectedSecFilters: any[]
  selectedRegFilters: any[]
  selectedCountryFilters: any[]
  filterOverlay: boolean
}
export class EventListings extends Component<EventsProps, EventsState> {
  state: Readonly<EventsState> = {
    activeMonth: '',
    filter: ['all', ''],
    filteredItems: [],
    isLoading: true,
    months: [],
    sort: 'upcoming',
    filSecReg: '',
    isFilterType: false,
    selectedSecFilters: [],
    selectedRegFilters: [],
    selectedCountryFilters: [],
    filterOverlay: false
  }

  componentDidMount = () => {
    this.processData()
  }

  componentDidUpdate(prevProps: { items: EventProps[] }) {
    // to update component basd on new props
    const { items } = this.props
    if (items !== prevProps.items) {
      this.processData()
    }
  }

  updateFilteredValue = (
    filter: string[],
    filteredSecList: any,
    filteredRegList: any,
    filteredCountryList: any
  ) => {
    let searchList: any = []
    let updatedSortvalue = ''
    const locationSearch =
      window.location.search === '?' ? '' : window.location.search
    /* eslint no-unused-expressions: [2, { allowShortCircuit: true }] */
    const replacedString =
      locationSearch &&
      locationSearch.replace(new RegExp('%20', 'g'), ' ').replace('?', '')
    searchList =
      replacedString && replacedString.length > 0 && replacedString.split('&')
    /* eslint-disable no-param-reassign  */
    searchList &&
      searchList.forEach((search: string) => {
        if (search !== '') {
          const [searchKey = '', searchValue = ''] = search.split('=')
          switch (searchKey) {
            case 'eventType':
              filter[1] = searchValue
              filter[0] = searchValue.toLowerCase()
              break
            case 'sort':
              updatedSortvalue = searchValue
              break
            case 'sector':
              filteredSecList.push(searchValue)
              break
            case 'region':
              filteredRegList.push(searchValue)
              break
            case 'country':
              filteredCountryList.push(searchValue)
              break
            default:
            // No default
          }
        }
      })
    filteredSecList &&
      filteredSecList.length > 0 &&
      this.setState({ selectedSecFilters: filteredSecList })
    filteredRegList &&
      filteredRegList.length > 0 &&
      this.setState({ selectedRegFilters: filteredRegList })
    filteredCountryList &&
      filteredCountryList.length > 0 &&
      this.setState({ selectedCountryFilters: filteredCountryList })
    return updatedSortvalue
  }

  sortAndReduce = (updatedSortValue: string, monthsList: any) => {
    const buildDate: number = Date.now()
    const { items } = this.props
    const { filter, sort } = this.state
    if (updatedSortValue === '') {
      updatedSortValue = sort
    }
    const sortedItem = [...items].sort((a, b) => {
      if (updatedSortValue === 'past')
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    })
    return (
      sortedItem &&
      sortedItem.reduce<EventProps[]>((r = [], item) => {
        const eventDate: number =
          new Date(item?.endDate).getTime() ||
          new Date(item.startDate).getTime()
        const sorted =
          updatedSortValue === 'upcoming' || updatedSortValue === ''
            ? buildDate <= eventDate
            : buildDate >= eventDate

        const filtered =
          filter[0] === 'all' ? true : includes(filter[1], item.eventType)
        if (sorted && filtered) {
          r.push(item)
          monthsList.push(FormatMonth(item.startDate))
        }
        return r
      }, [])
    )
  }

  processData = () => {
    const { filter } = this.state
    const monthsList: any = []
    const filteredSecList: any = []
    const filteredRegList: any = []
    const filteredCountryList: any = []
    const updatedSortValue = this.updateFilteredValue(
      filter,
      filteredSecList,
      filteredRegList,
      filteredCountryList
    )
    updatedSortValue && this.setState({ sort: updatedSortValue })
    const reducedItems = this.sortAndReduce(updatedSortValue, monthsList)
    const haveBothRegCountryFilter =
      filteredRegList &&
      filteredRegList.length > 0 &&
      filteredCountryList &&
      filteredCountryList.length > 0

    const haveRegionFilter = filteredRegList && filteredRegList.length > 0
    const haveCountryFilter =
      filteredCountryList && filteredCountryList.length > 0
    const haveSectorFilter = filteredSecList && filteredSecList.length > 0

    const filteredReturnArray = reducedItems.filter((item) => {
      let matches = false
      const sectorList = item.sectors
      const regionList = item.regions
      const countryList = item.countries

      const countryMatches = () => {
        matches =
          countryList.filter((countryItem) =>
            filteredCountryList.includes(countryItem.title)
          ).length > 0
      }

      const regionMatches = () => {
        matches =
          regionList.filter((regionItem) =>
            filteredRegList.includes(regionItem.title)
          ).length > 0
      }

      const sectorMatches = () => {
        matches =
          sectorList.filter((sectorItem) =>
            filteredSecList.includes(sectorItem.title)
          ).length > 0
      }

      const bothRegAndCountryMatches = () => {
        regionList.forEach((regItem) => {
          filteredRegList.includes(regItem.title) && countryMatches()
        })
      }

      const filteredByRegion = () => {
        if (haveBothRegCountryFilter) {
          regionList && countryList && bothRegAndCountryMatches()
        } else if (haveRegionFilter) {
          regionList && regionMatches()
        } else if (haveCountryFilter) {
          countryList && countryMatches()
        } else if (haveSectorFilter) {
          sectorList && sectorMatches()
        }
      }

      if (sectorList && haveSectorFilter) {
        sectorList.forEach((sectorItem) => {
          filteredSecList.includes(sectorItem.title) && filteredByRegion()
        })
      } else {
        filteredByRegion()
      }

      return matches
    })

    this.setState((prevState) => ({
      ...prevState,
      activeMonth: monthsList[0],
      filteredItems:
        (filteredSecList && filteredSecList.length > 0) ||
        (filteredRegList && filteredRegList.length > 0) ||
        (filteredCountryList && filteredCountryList.length > 0)
          ? filteredReturnArray
          : reducedItems,
      isLoading: false,
      months: monthsList
    }))
  }

  clickFilter = (type: string, include?: string[]) => {
    const data: any = [type, include]
    this.setState(
      (prevState) => ({
        ...prevState,
        filter: data,
        isFilterType: false,
        filterOverlay: false
      }),
      () => this.applyFilterUrl()
    )
  }

  clickSort = (type: string) => {
    this.setState(
      (prevState) => ({
        ...prevState,
        sort: type,
        isFilterType: false,
        filterOverlay: false
      }),
      () => this.applyFilterUrl()
    )
  }

  clickFilRegSec = (type: string) => {
    this.setState(
      (prevState) => ({
        ...prevState,
        filSecReg: type,
        isFilterType: true,
        filterOverlay: true
      }),
      () => this.processData()
    )
  }

  clickMonth = (month: string) => {
    this.setState((prevState) => ({
      ...prevState,
      activeMonth: month
    }))
  }

  applyFilterUrl = () => {
    const {
      filter,
      sort,
      selectedSecFilters,
      selectedRegFilters,
      selectedCountryFilters,
      filSecReg
    } = this.state
    const { eventLangSlug } = this.props
    const regionLangSlug = eventLangSlug !== 'en' ? eventLangSlug : ''
    let filterString = ''
    if (filter && filter.length > 0) {
      const [eventType = '', eventTypeURLParam = ''] = filter
      filterString =
        eventType !== 'all'
          ? filterString.concat(`eventType=${eventTypeURLParam}&`)
          : ''
    }
    if (sort && sort !== '' && sort !== 'upcoming') {
      filterString = filterString.concat(`sort=${sort}&`)
    }
    if (selectedSecFilters.length > 0) {
      selectedSecFilters.forEach((filterValue: string) => {
        filterString = filterString.concat(`sector=${filterValue}&`)
      })
    }
    if (selectedRegFilters.length > 0) {
      selectedRegFilters.forEach((filterValue: string) => {
        filterString = filterString.concat(`region=${filterValue}&`)
      })
    }
    if (selectedCountryFilters.length > 0) {
      selectedCountryFilters.forEach((filterValue: string) => {
        filterString = filterString.concat(`country=${filterValue}&`)
      })
    }
    const urlparams = regionLangSlug ? `/${regionLangSlug}/events?` : `/events?`
    if (filSecReg) {
      window.location.assign(`${urlparams}${filterString}`)
    } else if (window.location.pathname.includes('events')) {
      const newurl = `${window.location.protocol}//${window.location.host}${urlparams}${filterString}`
      window.history.pushState({ path: newurl }, '', newurl)
      this.processData()
    } else {
      this.processData()
    }
  }

  updateFilters = (
    isAlreadySelected: boolean,
    selectedItem: FilterSubItemProps
  ) => {
    const { selectedSecFilters, selectedRegFilters, selectedCountryFilters } =
      this.state
    const { filterType } = selectedItem
    let updatedSelectedSecFilters: any
    let updatedSelectedRegFilters: any
    let updateSelectedCountryFilters: any
    const updateSelection = (data: string[], applyUniqueSelection = false) => {
      let result: string[] = data
      if (isAlreadySelected) {
        result = data.filter((item) => item !== selectedItem.value)
      } else {
        if (applyUniqueSelection) result.shift()
        result.push(selectedItem.value)
      }
      return result
    }
    if (filterType === 'sector') {
      updatedSelectedSecFilters = updateSelection(selectedSecFilters)
      this.setState({ selectedSecFilters: updatedSelectedSecFilters })
    }
    if (filterType === 'region') {
      updatedSelectedRegFilters = updateSelection(selectedRegFilters)
      this.setState({ selectedRegFilters: updatedSelectedRegFilters })
    }
    if (filterType === 'country') {
      updateSelectedCountryFilters = updateSelection(selectedCountryFilters)
      this.setState({ selectedCountryFilters: updateSelectedCountryFilters })
    }
  }

  isItemSelected = (value: string) => {
    const {
      selectedRegFilters,
      selectedCountryFilters,
      selectedSecFilters,
      filSecReg
    } = this.state
    let result: boolean
    if (filSecReg === 'sector') {
      result = selectedSecFilters.includes(value)
    } else {
      result =
        selectedRegFilters.includes(value) ||
        selectedCountryFilters.includes(value)
    }
    return result
  }

  handleSubmitClick = () => {
    this.applyFilterUrl()
  }

  handleResetClick = () => {
    const { eventLangSlug } = this.props
    const regionLangSlug = eventLangSlug !== 'en' ? eventLangSlug : ''
    const urlparams = regionLangSlug ? `/${regionLangSlug}/events` : `/events`
    window.location.assign(urlparams)
  }

  handleOnCloseClick = () => {
    this.setState({ filterOverlay: false })
  }

  showFilters = () => {
    const { filSecReg } = this.state
    const { secFilType, regFilType } = this.props
    const { items, value } = filSecReg === 'sector' ? secFilType : regFilType
    return (
      <>
        <div className="event__overlay-title">{value}</div>
        <div className="event__filter-wrapper">
          {withScroller(
            <>
              <ul className="event__filter">
                {items &&
                  items.length &&
                  items.map((subItem) => (
                    <EventListingsFilterType
                      filterType={value}
                      filterItems={subItem}
                      isChecked={this.isItemSelected(subItem.value)}
                      updateFilters={this.updateFilters}
                      key={subItem.label}
                    />
                  ))}
              </ul>
            </>
          )}
        </div>
        <div className="button__wrapper">
          <button
            type="button"
            className="button button--6-1"
            onClick={this.handleResetClick}
            style={{ width: '200px' }}
          >
            Reset
          </button>
          <button
            type="button"
            className="button button--3"
            onClick={this.handleSubmitClick}
            style={{ width: '200px' }}
          >
            Submit
          </button>
        </div>
        <div
          onClick={this.handleOnCloseClick}
          onKeyUp={this.handleOnCloseClick}
          role="button"
          tabIndex={0}
          className="event__overlay-close"
        >
          <i className="fas fa-times" />
        </div>
      </>
    )
  }

  mapEvents = () => {
    const { filteredItems } = this.state
    return filteredItems.map((item: EventProps, index: number) => {
      const { startDate } = item
      const month = FormatMonth(startDate)
      const hash = Hash(['eventMap', startDate, index].join('#'))
      const fi: any = filteredItems[index - 1]
      const fii: any = filteredItems[index]
      const monthPrev = index > 0 ? fi.startDate : fii.startDate
      return (
        <Fragment key={hash}>
          {(index === 0 || FormatMonth(monthPrev) !== month) && (
            <div className="event__row--date event__row--date---1" id={month}>
              {month}
            </div>
          )}
          <EventListing {...item} />
        </Fragment>
      )
    })
  }

  getEventClassName = () => {
    const { filterOverlay } = this.state
    const { isHomePage, hideFilters } = this.props
    let eventSectionClassName: string
    if (isHomePage || hideFilters) {
      if (filterOverlay) {
        eventSectionClassName = 'section__events active'
      } else {
        eventSectionClassName = 'section__events'
      }
    } else if (filterOverlay) {
      eventSectionClassName = 'section__events section__events--header active'
    } else {
      eventSectionClassName = 'section__events section__events--header'
    }
    return eventSectionClassName
  }

  render() {
    const FRBackgroundImage =
      "url('https://images.ctfassets.net/03fbs7oah13w/1duv1G8KG9R9YS70znGcwt/d7f31c88d7e9c46e5034e4dc8477a0c4/fr-page-header-001.jpg')" +
      ',' +
      'linear-gradient(108deg, #3e5151 0%, #decba4 100%)'
    const {
      eventsDataLoading,
      hideFilters = false,
      bgImage = FRBackgroundImage
    } = this.props
    const {
      activeMonth,
      filter,
      filteredItems,
      isLoading,
      months,
      sort,
      filSecReg,
      filterOverlay,
      selectedRegFilters,
      selectedCountryFilters,
      selectedSecFilters
    } = this.state
    let filteredBytype = filSecReg
    if (!filterOverlay) {
      if (
        (selectedRegFilters.length > 0 || selectedCountryFilters.length > 0) &&
        selectedSecFilters.length === 0
      ) {
        filteredBytype = 'region'
      } else if (
        selectedSecFilters.length > 0 &&
        selectedRegFilters.length === 0
      ) {
        filteredBytype = 'sector'
      }
    }

    const bgImageStyle = {
      backgroundImage: bgImage
    }

    const eventClassName = this.getEventClassName()
    return (
      <section className={eventClassName}>
        <div className="event__background bg--blend" style={bgImageStyle} />
        <div className="wrapper--1 clearfix">
          <div className="event__wrapper--title">
            <Header className="heading--2" level={1}>
              Events
            </Header>
            {!isLoading && (
              <>
                <EventListingsFilters
                  filter={filter}
                  handleClick={this.clickFilter}
                  items={eventTypes}
                  filterOverlay={filterOverlay}
                  title="Event Type"
                />
                <EventListingsFilters
                  handleClick={this.clickSort}
                  items={sortTypes}
                  sort={sort}
                  filterOverlay={filterOverlay}
                  title="Sort"
                />
                <div hidden={hideFilters}>
                  <EventListingsFilters
                    handleClick={this.clickFilRegSec}
                    items={filSecRegTypes}
                    filterOverlay={filterOverlay}
                    filteredBytype={filteredBytype}
                    title="Filter"
                  />
                </div>
              </>
            )}
          </div>
          <div className="quicktabs-tabpage">
            <div
              id="event__wrapper--row-wrapper"
              className="event__wrapper--row-wrapper"
            >
              {withScroller(
                <div className="event__wrapper--row">
                  <div className="event__row--group">
                    {filteredItems && filteredItems.length ? (
                      this.mapEvents()
                    ) : (
                      <div className="event__row--message">
                        {eventsDataLoading ? 'Loading Events...' : 
                          <>
                            <span> More events coming soon. Browse past events and </span>
                            <span style={{fontWeight:"bold"}}> watch webinar replays </span>
                          </>
                        }
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <EventListingsMonths
              active={activeMonth}
              handleClick={this.clickMonth}
              months={months}
              sort={sort}
            />
          </div>
          <div className="event__overlay" hidden={!filterOverlay}>
            {this.showFilters()}
          </div>
        </div>
      </section>
    )
  }
}
